<template>
  <div class="fa-launcher-root">
    <transition name="fa-widget-expand">
      <section
        v-if="hasOpened"
        v-show="isOpen"
        class="fa-widget-panel"
        :class="{
          'fa-widget-panel--mobile': isMobile,
          'fa-widget-panel--input-focused': isMobile && inputFocused,
        }"
        :style="panelStyle"
        role="dialog"
        aria-label="Fleet Assistant"
      >
        <header
          class="fa-widget-header"
          :class="{ 'fa-widget-header--draggable': canMoveResize && !isMaximized }"
          @mousedown.prevent="startDrag"
        >
          <div class="fa-widget-title">
            <!-- user-selected avatar in the window title (inverted styling for
                 the bubble variant: white bubble outlined in black). plays its
                 attention animation while the assistant is working on a reply,
                 so the icon "talks" while it thinks. -->
            <fa_avatar
              class="fa-chat-svg fa-chat-svg--inverted"
              :variant="avatarVariant"
              :active="assistantBusy"
              inverted
            />
            <span>Fleet Assistant</span>
          </div>
          <div class="fa-widget-header-actions">
            <b-button
              v-if="canMoveResize"
              size="sm"
              variant="link"
              class="fa-widget-btn-icon"
              :aria-label="isMaximized ? 'Restore window' : 'Maximize'"
              :title="isMaximized ? 'Restore' : 'Maximize'"
              @mousedown.stop
              @click="toggleMaximize"
            >
              <i :class="isMaximized ? 'fa fa-compress' : 'fa fa-expand'" aria-hidden="true"></i>
            </b-button>
            <b-button
              size="sm"
              variant="link"
              class="fa-widget-close"
              aria-label="Close Fleet Assistant"
              @mousedown.stop
              @click="closePanel"
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </b-button>
          </div>
        </header>
        <div class="fa-widget-body">
          <fleet_assistant ref="fleetAssistant" embedded @showAlert="forwardAlert" @input-focus-change="onInputFocusChange" @busy-change="onAssistantBusyChange" @response-complete="onAssistantResponseComplete" />
        </div>
        <!-- four-corner resize grips. all four share the same subtle
             diagonal-stripe marker so the affordance reads the same at
             every corner. .stop on the top grips keeps the header's
             drag handler from firing when they sit over the header.
             gated by canMoveResize (hidden on mobile + while maximized). -->
        <div
          v-if="canMoveResize && !isMaximized"
          class="fa-widget-resize-handle fa-widget-resize-handle--nw"
          aria-label="Resize from top-left"
          @mousedown.prevent.stop="startResize($event, 'nw')"
        />
        <div
          v-if="canMoveResize && !isMaximized"
          class="fa-widget-resize-handle fa-widget-resize-handle--ne"
          aria-label="Resize from top-right"
          @mousedown.prevent.stop="startResize($event, 'ne')"
        />
        <div
          v-if="canMoveResize && !isMaximized"
          class="fa-widget-resize-handle fa-widget-resize-handle--sw"
          aria-label="Resize from bottom-left"
          @mousedown.prevent="startResize($event, 'sw')"
        />
        <div
          v-if="canMoveResize && !isMaximized"
          class="fa-widget-resize-handle fa-widget-resize-handle--se"
          aria-label="Resize from bottom-right"
          @mousedown.prevent="startResize($event, 'se')"
        />
      </section>
    </transition>

    <transition name="fa-launcher-pulse">
      <button
        v-show="!isOpen && !pillHidden"
        type="button"
        class="fa-widget-toggle"
        :class="[
          {
            'fa-widget-toggle--icon-only': hasUsedLauncher,
            'fa-widget-toggle--tutorial-glow': tutorialGlow,
          },
          tutorialGlow ? ('fa-highlight-style-' + highlightStyle) : ''
        ]"
        :aria-label="hasUnread ? 'Open Fleet Assistant (new reply)' : (hasUsedLauncher ? 'Open Fleet Assistant' : 'Ask Fleet Assistant')"
        @click="openPanel"
      >
        <!-- user-selected avatar. the bubble variant keeps the inverted-puck
             outline treatment (stroke + dots in currentColor over the pill's
             pale bg). switches to its attention animation when the tutorial
             halo is running, or when a reply finished while the panel was
             closed and is waiting to be read. -->
        <fa_avatar
          class="fa-chat-svg"
          :variant="avatarVariant"
          :active="tutorialGlow || hasUnread"
        />
        <span v-if="!hasUsedLauncher">Ask Fleet Assistant</span>
      </button>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import fleet_assistant from './fleet_assistant.vue'
import fa_avatar from './fa_avatar.vue'

const MIN_WIDTH = 360
const MIN_HEIGHT = 400
const DEFAULT_WIDTH = 460
const DEFAULT_HEIGHT = 760
const MARGIN = 24
const MOBILE_BREAKPOINT = 768
const STORAGE_KEY = 'faLauncherState'

export default {
  name: 'fleet_assistant_launcher',
  components: {
    fleet_assistant,
    fa_avatar,
  },
  props: {
    // set by the horizon shell: on phone widths the top bar carries the FA
    // button, so the floating pill hides there. the panel and the $root
    // open/close handlers stay mounted either way -- the bar button opens
    // the same panel through the 'open-fleet-assistant' event.
    barLaunches: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      hasOpened: false,
      hasUsedLauncher: false,
      isMaximized: false,
      keydownHandler: null,
      resizeHandler: null,
      isMobile: false,
      // true under the nav's 992px breakpoint (not the panel's 768): the
      // horizon bar shows its FA button exactly while the rail is in drawer
      // mode, so the pill's hide tracks the same line
      barViewport: false,
      panelLeft: null,
      panelTop: null,
      panelWidth: DEFAULT_WIDTH,
      panelHeight: DEFAULT_HEIGHT,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartLeft: 0,
      dragStartTop: 0,
      resizing: false,
      resizeMode: 'se', // 'se' (anchor top-left) or 'nw' (anchor bottom-right)
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      resizeStartLeft: 0,
      resizeStartTop: 0,
      boundDragMove: null,
      boundDragEnd: null,
      boundResizeMove: null,
      boundResizeEnd: null,
      // true while the "Ask Fleet Assistant" pill is running its attention-grabbing
      // rainbow-halo pre-open animation. reset as soon as openPanel() is called.
      tutorialGlow: false,
      // true when the assistant finished a reply while the panel was closed —
      // the launcher avatar plays its attention animation until the panel is
      // opened again. wired via @response-complete from the child.
      hasUnread: false,
      // true while the assistant is working on a reply (child's loading flag,
      // wired via @busy-change). drives the header avatar's "talking" state.
      assistantBusy: false,
      // true while the user is typing in the FA input textarea. on mobile
      // we use this to collapse the panel header so the message list +
      // input fill the visible viewport cleanly when the iOS keyboard is
      // open (otherwise the header gets pushed off-screen, leaving an
      // awkward blank strip). wired via @input-focus-change emitted by the
      // child fleet_assistant component.
      inputFocused: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // per-user tutorial preference, set in Manage Profile. falls back to 'full'
    // for users who haven't touched the setting (either never saved, or signed
    // in before the preferences field existed on the backend).
    tutorialMode() {
      const p = this.user && this.user.preferences
      const v = p && p.fa_tutorial_mode
      return (v === 'off' || v === 'fast' || v === 'full' || v === 'slow') ? v : 'full'
    },
    // which animation runs on the launcher while the tutorial is attracting
    // attention.
    //   pulse   — rainbow halo pulsing (legacy default)
    //   chase   — saturated rainbow arc sweeping the perimeter like a snake
    //   ripple  — concentric radar-ping rings radiating outward
    //   magenta — single-color magenta glow pulsing softly
    //   aurora  — slow drifting green/teal/purple wash, like northern lights
    // unknown / missing → 'pulse'.
    highlightStyle() {
      const p = this.user && this.user.preferences
      const v = p && p.highlight_style
      return (v === 'chase' || v === 'ripple' || v === 'pulse' || v === 'magenta' || v === 'aurora') ? v : 'pulse'
    },
    // which avatar the assistant wears, set in Manage Profile. unknown /
    // missing → the classic chat bubble.
    avatarVariant() {
      const p = this.user && this.user.preferences
      const v = p && p.fa_icon
      return (v === 'cloud' || v === 'stapler' || v === 'nova' || v === 'plasma') ? v : 'bubble'
    },
    canMoveResize() {
      return !this.isMobile && this.hasOpened && this.isOpen
    },
    pillHidden() {
      return this.barLaunches && this.barViewport
    },
    panelStyle() {
      if (this.isMobile) {
        return {}
      }
      if (this.isMaximized) {
        return {
          left: '0',
          top: '0',
          right: '0',
          bottom: '0',
          width: '100%',
          height: '100%',
          borderRadius: '0',
        }
      }
      const left = this.panelLeft != null ? this.panelLeft : (window.innerWidth - MARGIN - this.panelWidth)
      const top = this.panelTop != null ? this.panelTop : (window.innerHeight - MARGIN - this.panelHeight)
      return {
        left: left + 'px',
        top: top + 'px',
        width: this.panelWidth + 'px',
        height: this.panelHeight + 'px',
      }
    },
  },
  watch: {
    // the horizon bar's FA button mirrors the pill's unread attention state;
    // a $root event keeps the two decoupled (the bar button renders only on
    // phone widths, the pill everywhere else)
    hasUnread(v) {
      this.$root.$emit('fa-unread-change', !!v)
    },
  },
  mounted() {
    this.keydownHandler = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePanel()
      }
    }
    window.addEventListener('keydown', this.keydownHandler)

    this.updateMobile()
    this.resizeHandler = () => this.updateMobile()
    window.addEventListener('resize', this.resizeHandler)

    this.boundDragMove = (e) => this.onDragMove(e)
    this.boundDragEnd = () => this.endDrag()
    this.boundResizeMove = (e) => this.onResizeMove(e)
    this.boundResizeEnd = () => this.endResize()

    this.loadPanelState()

    // listen for programmatic requests to open FA (e.g. "Ask Fleet Assistant"
    // pill in the help center no-results pane, or a follow-up chip in an FA
    // answer). payload:
    //   { initialQuery: string, autoSend?: boolean }
    //   autoSend=true sends the prompt immediately (used by [Label](ask:…)
    //   chips so they behave like the user asked); otherwise it just pre-fills.
    this._openFaHandler = (opts = {}) => {
      if (!this.isOpen) this.openPanel()
      this.$nextTick(() => {
        const fa = this.$refs.fleetAssistant
        if (!fa || !opts.initialQuery) return
        if (opts.autoSend && typeof fa.sendSuggestedPrompt === 'function') {
          fa.sendSuggestedPrompt(opts.initialQuery)
        } else if (typeof fa.setInitialMessage === 'function') {
          fa.setInitialMessage(opts.initialQuery)
        }
      })
    }
    this.$root.$on('open-fleet-assistant', this._openFaHandler)

    // tutorial-mode: rainbow halo on the launcher → brief pause → open panel →
    // animated typing of the message into the FA input. triggered by the
    // "Ask Fleet Assistant" pill in the help center no-results pane.
    // payload: { message: string }
    this._tutorialHandler = (opts = {}) => {
      const text = (opts && typeof opts.message === 'string') ? opts.message : ''
      if (!text.trim()) return
      this._runTutorialSequence(text)
    }
    this.$root.$on('open-fleet-assistant-tutorial', this._tutorialHandler)

    // programmatic close request — fired by the AI usage request modal
    // before it opens, so the FA panel doesn't cover the modal on mobile
    // (FA sits at z-index 2147483001, which wins the stacking war by
    // default). no-op if panel isn't currently open.
    this._closeFaHandler = () => {
      if (this.isOpen) this.closePanel()
    }
    this.$root.$on('close-fleet-assistant', this._closeFaHandler)
  },
  beforeDestroy() {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this._openFaHandler) {
      this.$root.$off('open-fleet-assistant', this._openFaHandler)
    }
    if (this._tutorialHandler) {
      this.$root.$off('open-fleet-assistant-tutorial', this._tutorialHandler)
    }
    if (this._closeFaHandler) {
      this.$root.$off('close-fleet-assistant', this._closeFaHandler)
    }
    this.removeDragListeners()
    this.removeResizeListeners()
  },
  methods: {
    loadPanelState() {
      try {
        const raw = window.localStorage && window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const s = JSON.parse(raw)
        if (s.hasUsedLauncher === true) this.hasUsedLauncher = true
        if (!this.isMobile && s.panelWidth != null && typeof s.panelWidth === 'number') {
          this.panelWidth = Math.max(MIN_WIDTH, Math.min(window.innerWidth - MARGIN, s.panelWidth))
        }
        if (!this.isMobile && s.panelHeight != null && typeof s.panelHeight === 'number') {
          this.panelHeight = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - MARGIN, s.panelHeight))
        }
        if (s.isMaximized === true) this.isMaximized = true
        // restore position from corner anchor (adapts to window resize between sessions)
        if (!this.isMobile && s.anchor && s.anchor.anchorH && s.anchor.anchorV) {
          const w = window.innerWidth
          const h = window.innerHeight
          const distH = Math.max(MARGIN, s.anchor.distH || MARGIN)
          const distV = Math.max(MARGIN, s.anchor.distV || MARGIN)
          if (s.anchor.anchorH === 'right') {
            this.panelLeft = w - this.panelWidth - distH
          } else {
            this.panelLeft = distH
          }
          if (s.anchor.anchorV === 'bottom') {
            this.panelTop = h - this.panelHeight - distV
          } else {
            this.panelTop = distV
          }
        } else if (!this.isMobile) {
          // fallback: use absolute positions (legacy state)
          if (s.panelLeft != null && typeof s.panelLeft === 'number') this.panelLeft = s.panelLeft
          if (s.panelTop != null && typeof s.panelTop === 'number') this.panelTop = s.panelTop
        }
        this.fitPanelToViewport()
      } catch (_) {}
    },
    _computeAnchor() {
      // determine which corner the panel is closest to and store distances from that corner
      if (this.panelLeft == null || this.panelTop == null) return null
      const w = window.innerWidth
      const h = window.innerHeight
      const centerX = this.panelLeft + this.panelWidth / 2
      const centerY = this.panelTop + this.panelHeight / 2
      const isRight = centerX > w / 2
      const isBottom = centerY > h / 2
      return {
        anchorH: isRight ? 'right' : 'left',
        anchorV: isBottom ? 'bottom' : 'top',
        distH: isRight ? (w - this.panelLeft - this.panelWidth) : this.panelLeft,
        distV: isBottom ? (h - this.panelTop - this.panelHeight) : this.panelTop,
      }
    },
    savePanelState() {
      try {
        if (!window.localStorage) return
        const anchor = this._computeAnchor()
        const s = {
          hasUsedLauncher: this.hasUsedLauncher,
          panelLeft: this.panelLeft,
          panelTop: this.panelTop,
          panelWidth: this.panelWidth,
          panelHeight: this.panelHeight,
          isMaximized: this.isMaximized,
          anchor: anchor,
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
      } catch (_) {}
    },
    updateMobile() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT
      this.barViewport = window.innerWidth < 992
      if (this.isMobile) return
      // shrinking or rotating with the panel open must pull the whole
      // panel (incl. its right-edge grips) back into view, not just the
      // grabbable strip the drag clamp guarantees
      if (this.isOpen && !this.isMaximized) {
        this.fitPanelToViewport()
      } else {
        this.clampPanelPosition()
      }
    },
    clampPanelPosition() {
      const maxLeft = window.innerWidth - MIN_WIDTH - MARGIN
      const maxTop = window.innerHeight - MIN_HEIGHT - MARGIN
      if (this.panelLeft != null) {
        this.panelLeft = Math.max(MARGIN, Math.min(maxLeft, this.panelLeft))
      }
      if (this.panelTop != null) {
        this.panelTop = Math.max(MARGIN, Math.min(maxTop, this.panelTop))
      }
    },
    // the drag clamp above only guarantees a grabbable strip stays visible;
    // restoring desktop-saved geometry on a smaller screen (ipad) needs the
    // WHOLE panel back in view, so restore/open fit size + position fully.
    fitPanelToViewport() {
      const w = window.innerWidth
      const h = window.innerHeight
      // a viewport shorter than min-size + margins (landscape phones are
      // wide enough to take this desktop branch) must win over the
      // minimums, or the input row lands below the fold -- shrink to fit
      // rather than clip
      const maxW = Math.max(120, w - MARGIN * 2)
      const maxH = Math.max(120, h - MARGIN * 2)
      this.panelWidth = Math.max(Math.min(MIN_WIDTH, maxW), Math.min(maxW, this.panelWidth))
      this.panelHeight = Math.max(Math.min(MIN_HEIGHT, maxH), Math.min(maxH, this.panelHeight))
      if (this.panelLeft != null) {
        this.panelLeft = Math.max(MARGIN, Math.min(w - this.panelWidth - MARGIN, this.panelLeft))
      }
      if (this.panelTop != null) {
        this.panelTop = Math.max(MARGIN, Math.min(h - this.panelHeight - MARGIN, this.panelTop))
      }
    },
    openPanel() {
      this.hasOpened = true
      this.hasUsedLauncher = true
      this.isOpen = true
      this.hasUnread = false
      if (!this.isMobile) {
        if (this.panelLeft == null || this.panelTop == null) {
          this.panelLeft = window.innerWidth - MARGIN - this.panelWidth
          this.panelTop = window.innerHeight - MARGIN - this.panelHeight
        }
        this.fitPanelToViewport()
      }
      // prevent outer page scroll when mobile panel is open (iOS keyboard fix)
      if (this.isMobile) {
        document.body.style.overflow = 'hidden'
      }
      this.savePanelState()
      this.$nextTick(() => {
        const fa = this.$refs.fleetAssistant
        // skip auto-focus on mobile: it would immediately pop the iOS keyboard,
        // covering the welcome + suggested prompts before the user has a chance
        // to see them. on desktop we still auto-focus so the user can start
        // typing right away (no keyboard cost there).
        if (fa && typeof fa.focusInput === 'function' && !this.isMobile) fa.focusInput()
        if (fa && typeof fa.reloadSettings === 'function') fa.reloadSettings()
      })
    },
    closePanel() {
      this.isOpen = false
      document.body.style.overflow = ''
      this.savePanelState()
    },
    forwardAlert(message, variant, autoclose) {
      this.$emit('showAlert', message, variant, autoclose)
    },
    onInputFocusChange(focused) {
      this.inputFocused = !!focused
    },
    onAssistantBusyChange(busy) {
      this.assistantBusy = !!busy
    },
    onAssistantResponseComplete() {
      // a reply landed while the panel was closed (the chat stays mounted
      // behind v-show, so in-flight requests finish even after a close) —
      // have the launcher avatar wave the user back over.
      if (!this.isOpen) this.hasUnread = true
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized
      this.savePanelState()
    },
    startDrag(e) {
      if (!this.canMoveResize || this.isMaximized || this.dragging || this.resizing) return
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragStartLeft = this.panelLeft != null ? this.panelLeft : (window.innerWidth - MARGIN - this.panelWidth)
      this.dragStartTop = this.panelTop != null ? this.panelTop : (window.innerHeight - MARGIN - this.panelHeight)
      document.addEventListener('mousemove', this.boundDragMove)
      document.addEventListener('mouseup', this.boundDragEnd)
    },
    onDragMove(e) {
      if (!this.dragging) return
      const dx = e.clientX - this.dragStartX
      const dy = e.clientY - this.dragStartY
      let left = this.dragStartLeft + dx
      let top = this.dragStartTop + dy
      const maxLeft = window.innerWidth - this.panelWidth - MARGIN
      const maxTop = window.innerHeight - this.panelHeight - MARGIN
      left = Math.max(MARGIN, Math.min(maxLeft, left))
      top = Math.max(MARGIN, Math.min(maxTop, top))
      this.panelLeft = left
      this.panelTop = top
    },
    endDrag() {
      this.dragging = false
      this.removeDragListeners()
      this.savePanelState()
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this.boundDragMove)
      document.removeEventListener('mouseup', this.boundDragEnd)
    },
    startResize(e, mode = 'se') {
      if (!this.canMoveResize || this.resizing || this.dragging) return
      e.stopPropagation()
      this.resizing = true
      this.resizeMode = mode
      this.resizeStartX = e.clientX
      this.resizeStartY = e.clientY
      this.resizeStartWidth = this.panelWidth
      this.resizeStartHeight = this.panelHeight
      this.resizeStartLeft = this.panelLeft != null ? this.panelLeft : (window.innerWidth - MARGIN - this.panelWidth)
      this.resizeStartTop = this.panelTop != null ? this.panelTop : (window.innerHeight - MARGIN - this.panelHeight)
      document.addEventListener('mousemove', this.boundResizeMove)
      document.addEventListener('mouseup', this.boundResizeEnd)
    },
    onResizeMove(e) {
      if (!this.resizing) return
      const dx = e.clientX - this.resizeStartX
      const dy = e.clientY - this.resizeStartY
      const mode = this.resizeMode
      const startLeft = this.resizeStartLeft
      const startTop = this.resizeStartTop
      const startW = this.resizeStartWidth
      const startH = this.resizeStartHeight
      const rightEdge = startLeft + startW
      const bottomEdge = startTop + startH
      const viewportMaxRight = window.innerWidth - MARGIN
      const viewportMaxBottom = window.innerHeight - MARGIN

      // horizontal axis: which edge moves and what it clamps against
      if (mode === 'nw' || mode === 'sw') {
        // anchor the right edge, move the LEFT edge
        const maxLeft = rightEdge - MIN_WIDTH
        const newLeft = Math.max(MARGIN, Math.min(maxLeft, startLeft + dx))
        this.panelLeft = newLeft
        this.panelWidth = rightEdge - newLeft
      } else {
        // 'ne' or 'se' — anchor the left edge, move the RIGHT edge
        const maxW = viewportMaxRight - startLeft
        const newW = Math.max(MIN_WIDTH, Math.min(maxW, startW + dx))
        this.panelLeft = startLeft
        this.panelWidth = newW
      }

      // vertical axis
      if (mode === 'nw' || mode === 'ne') {
        // anchor the bottom edge, move the TOP edge
        const maxTop = bottomEdge - MIN_HEIGHT
        const newTop = Math.max(MARGIN, Math.min(maxTop, startTop + dy))
        this.panelTop = newTop
        this.panelHeight = bottomEdge - newTop
      } else {
        // 'sw' or 'se' — anchor the top edge, move the BOTTOM edge
        const maxH = viewportMaxBottom - startTop
        const newH = Math.max(MIN_HEIGHT, Math.min(maxH, startH + dy))
        this.panelTop = startTop
        this.panelHeight = newH
      }
    },
    endResize() {
      this.resizing = false
      this.removeResizeListeners()
      this.savePanelState()
    },
    removeResizeListeners() {
      document.removeEventListener('mousemove', this.boundResizeMove)
      document.removeEventListener('mouseup', this.boundResizeEnd)
    },
    async _runTutorialSequence(text) {
      const wait = (ms) => new Promise((r) => setTimeout(r, ms))
      const mode = this.tutorialMode

      // "off" — drop the message in instantly, no halo, no typing animation.
      if (mode === 'off') {
        if (!this.isOpen) this.openPanel()
        await this.$nextTick()
        const fa = this.$refs.fleetAssistant
        if (fa && typeof fa.setInitialMessage === 'function') {
          fa.setInitialMessage(text)
        }
        return
      }

      // launcher-halo window + inter-phase waits, tuned per preset.
      // "slow"  — extra-long halo and settle so the user can register each step
      // "full"  — default cadence
      // "fast"  — skip the launcher halo, short settle
      const haloMs    = mode === 'slow' ? 1600 : mode === 'full' ? 850 : 0
      const postHaloMs = mode === 'slow' ? 240 : mode === 'full' ? 120 : 0
      const settleMs  = mode === 'slow' ? 520 : mode === 'fast' ? 180 : 360

      if (!this.isOpen) {
        if (haloMs > 0) {
          this.tutorialGlow = true
          await wait(haloMs)
          this.tutorialGlow = false
          await wait(postHaloMs)
        }
        this.openPanel()
        await wait(settleMs)
      } else {
        await this.$nextTick()
      }
      const fa = this.$refs.fleetAssistant
      if (fa && typeof fa.typeMessage === 'function') {
        await fa.typeMessage(text, { speed: mode })
      } else if (fa && typeof fa.setInitialMessage === 'function') {
        // graceful fallback if typeMessage isn't available
        fa.setInitialMessage(text)
      }
    },
  },
}
</script>

<style scoped>
.fa-launcher-root {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483000;
}

.fa-widget-toggle {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483001;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  /* inverted-puck treatment: saturated FA-blue outline + glyph + label on
     pale FA-blue fill. border is thicker than the notification pucks (3px
     vs 1.5px) so the launcher still reads as "call to action" — the
     outlined style is quieter than the old solid-blue gradient pill, so
     the thicker edge keeps it as the dominant CTA in the bottom-right. */
  border: 3px solid #6cb1de;
  border-radius: 999px;
  /* frosted-white glass: translucent white base + heavy backdrop blur +
     saturation boost. over dark scrims it reads as a bright frosty pill
     (FA-blue border/text stays readable); over light content the white is
     subtle and the FA-blue elements still dominate. the saturate(1.6)
     amplifies whatever shows through so the glass feels alive rather than
     dead-flat. safari needs the -webkit- prefix. */
  background: rgba(255, 255, 255, 0.45);
  -webkit-backdrop-filter: blur(12px) saturate(1.6);
  backdrop-filter: blur(12px) saturate(1.6);
  color: var(--fi-brand);
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 122, 199, 0.20), 0 1px 2px rgba(0, 122, 199, 0.10);
  cursor: pointer;
  pointer-events: auto;
  overflow: hidden;
}

/* pastel shimmer — hover-only with smooth opacity crossfade. the ::before
   layer is always animating (cheap, runs even when invisible) so the
   shimmer is at a random-looking phase whenever hover begins.
   loop-seamlessness: the ::before is 200% wide and holds TWO full pastel
   cycles end-to-end; translating it by -50% (exactly one cycle) puts the
   visible slice back at a frame identical to the starting slice, so the
   loop has no visible seam. */
.fa-widget-toggle::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 100%;
  /* slight off-horizontal tilt (100deg vs pure 90deg) so the pastel bands
     slope gently — feels more organic than a perfectly vertical stripe
     sliding sideways. loop seamlessness is unaffected; the tilt just
     shears the already-periodic pattern. */
  background: linear-gradient(
    100deg,
    rgba(255, 182, 193, 0.30)  0%,
    rgba(186, 225, 255, 0.30) 10%,
    rgba(186, 255, 216, 0.30) 20%,
    rgba(255, 236, 179, 0.30) 30%,
    rgba(228, 186, 255, 0.30) 40%,
    rgba(255, 182, 193, 0.30) 50%,   /* end of cycle 1 = start of cycle 2 */
    rgba(186, 225, 255, 0.30) 60%,
    rgba(186, 255, 216, 0.30) 70%,
    rgba(255, 236, 179, 0.30) 80%,
    rgba(228, 186, 255, 0.30) 90%,
    rgba(255, 182, 193, 0.30) 100%
  );
  animation: fa-toggle-pastel-shimmer 6s linear infinite;
  opacity: 0;
  transition: opacity 350ms ease;
  pointer-events: none;
}

.fa-widget-toggle:hover::before {
  opacity: 1;
}

/* keep the glyph + label above the shimmer layer. */
.fa-widget-toggle > * {
  position: relative;
  z-index: 1;
}

@keyframes fa-toggle-pastel-shimmer {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

.fa-widget-toggle--icon-only {
  /* pinned circle: identical footprint no matter which avatar is selected
     (padding-driven sizing made the circle float between 42 and 48 as the
     art size varied). the avatar fills most of it — 30px bubble / 36px
     square art vs the old 26px-in-50px look. */
  padding: 0;
  width: 48px;
  height: 48px;
  justify-content: center;
}

.fa-widget-toggle--icon-only .fa,
.fa-widget-toggle--icon-only .fa-chat-svg {
  margin: 0;
}

/* chat-bubble + typing-dots glyph. path geometry:
   - viewBox 24x24; body rectangle y=3 to y=17 (body height 14)
   - tail extends y=17 to y=21 (4 units), hanging below the body
   in text contexts, vertical-align shifts the SVG down so the body rectangle's
   top/bottom align with the text cap-top/baseline and the tail dangles below
   the baseline. see per-context overrides below for icon-only (circle) use. */
.fa-chat-svg {
  width: 1.4em;
  height: 1.4em;
  vertical-align: -0.5em;
  flex-shrink: 0;
}
.fa-widget-toggle--icon-only .fa-chat-svg {
  width: 30px;
  height: 30px;
  vertical-align: middle;
  /* body rect is positioned higher than viewBox center (y=3 to y=17 inside a
     0-24 viewBox), so a small downward translate brings the body rectangle's
     mathematical center onto the circle's center. the tail then hangs below
     the midline — which is exactly what we want for a chat-bubble glyph. */
  transform: translateY(2px);
}
/* header title icon — matches the surrounding 16px title weight cleanly. */
.fa-widget-title .fa-chat-svg {
  width: 20px;
  height: 20px;
  vertical-align: -0.4em;
}

/* the bubble-specific alignment nudges above assume the chat-bubble's tail
   geometry. the cloud/stapler avatars are centered square art, so they sit
   on the box center with no nudge — and they carry more internal whitespace
   than the bubble glyph, so each context gives them a larger box to land at
   the same perceived size. */
.fa-chat-svg.fa-avatar--cloud,
.fa-chat-svg.fa-avatar--stapler,
.fa-chat-svg.fa-avatar--nova,
.fa-chat-svg.fa-avatar--plasma {
  width: 1.7em;
  height: 1.7em;
  vertical-align: -0.45em;
}
.fa-widget-toggle--icon-only .fa-chat-svg.fa-avatar--cloud,
.fa-widget-toggle--icon-only .fa-chat-svg.fa-avatar--stapler,
.fa-widget-toggle--icon-only .fa-chat-svg.fa-avatar--nova,
.fa-widget-toggle--icon-only .fa-chat-svg.fa-avatar--plasma {
  width: 36px;
  height: 36px;
  transform: none;
}
.fa-widget-title .fa-chat-svg.fa-avatar--cloud,
.fa-widget-title .fa-chat-svg.fa-avatar--stapler,
.fa-widget-title .fa-chat-svg.fa-avatar--nova,
.fa-widget-title .fa-chat-svg.fa-avatar--plasma {
  width: 24px;
  height: 24px;
  vertical-align: -0.45em;
}

/* rainbow attention-grabber used by the "Ask Fleet Assistant" tutorial sequence.
   matches the help-flash cadence used by the topic-suggestion pills — fast cycle
   through 6 hues so it reads as zippy rather than a slow pulse. */
.fa-widget-toggle--tutorial-glow {
  animation: fa-toggle-tutorial-halo 0.8s ease infinite;
}

@keyframes fa-toggle-tutorial-halo {
  0%   { box-shadow: 0 0 0 5px rgba(255, 0, 0, 0.55),   0 0 24px 8px rgba(255, 0, 0, 0.4),   0 4px 12px rgba(0, 92, 153, 0.28); }
  16%  { box-shadow: 0 0 0 5px rgba(255, 165, 0, 0.55), 0 0 24px 8px rgba(255, 165, 0, 0.4), 0 4px 12px rgba(0, 92, 153, 0.28); }
  33%  { box-shadow: 0 0 0 5px rgba(255, 220, 0, 0.55), 0 0 24px 8px rgba(255, 220, 0, 0.4), 0 4px 12px rgba(0, 92, 153, 0.28); }
  50%  { box-shadow: 0 0 0 5px rgba(0, 180, 0, 0.55),   0 0 24px 8px rgba(0, 180, 0, 0.4),   0 4px 12px rgba(0, 92, 153, 0.28); }
  66%  { box-shadow: 0 0 0 5px rgba(0, 100, 255, 0.55), 0 0 24px 8px rgba(0, 100, 255, 0.4), 0 4px 12px rgba(0, 92, 153, 0.28); }
  83%  { box-shadow: 0 0 0 5px rgba(140, 0, 255, 0.55), 0 0 24px 8px rgba(140, 0, 255, 0.4), 0 4px 12px rgba(0, 92, 153, 0.28); }
  100% { box-shadow: 0 0 0 5px rgba(255, 0, 0, 0.55),   0 0 24px 8px rgba(255, 0, 0, 0.4),   0 4px 12px rgba(0, 92, 153, 0.28); }
}

/* CHASE variant — a rainbow arc sweeping around the outside of the launcher
   like a snake chasing its tail.

   implementation: a ::before pseudo-element sized slightly larger than the
   button carries a conic-gradient rainbow arc. the mask-composite trick
   punches out the inner content-box, leaving only a ring visible.

   the sweep is driven by animating a typed custom property (@property
   --fa-chase-angle) that's wired into the conic-gradient's `from` angle,
   so the gradient rotates WITHIN the ring without rotating the ring
   itself. prior implementation used `transform: rotate` on the pseudo,
   which made the whole pill-shaped ring spin — reading as a rotating
   rectangle rather than a snake tracing the button's edge. */
/* CHASE — unbroken rainbow continuously chasing around the control's edge,
   like LED chaser lights on a frame. six full rainbow cycles wrapped around
   the ring so the pattern reads the same regardless of control size (small
   launcher, wide input row, preview box all get six loops of rainbow).

   the `from` angle is driven by @property --fa-chase-angle so ONLY the
   gradient rotates, not the pseudo-element (a rotating pseudo would spin
   non-circle controls as a whole rectangle). */
@property --fa-chase-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-chase {
  animation: none;
  overflow: visible;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-chase::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: inherit;
  padding: 4px;
  /* 12 color stops per cycle (6 rainbow anchors + 6 midpoints) so the hue
     progression through red→orange→yellow→green→cyan→blue→indigo→violet→
     magenta→red reads as one continuous, saturated rainbow without any
     muddy midpoints in the blend. a 60° cycle + repeating means six full
     rainbow wraps around the ring — same angular density at any control
     size, so a small circle and a wide pill both show six rainbow loops. */
  background: repeating-conic-gradient(
    from var(--fa-chase-angle),
    #ff2d6d   0deg,   /* red */
    #ff5a3a   5deg,
    #ff8a1f  10deg,   /* orange */
    #ffb820  15deg,
    #ffe21f  20deg,   /* yellow */
    #8fdd4a  25deg,
    #2bd96d  30deg,   /* green */
    #2bd3bd  35deg,
    #1e9bff  40deg,   /* blue */
    #6a6dff  45deg,
    #b94dff  50deg,   /* violet */
    #ff3db9  55deg,
    #ff2d6d  60deg    /* back to red — clean seam */
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  filter: saturate(1.25) drop-shadow(0 0 5px rgba(255, 255, 255, 0.45));
  /* rotate one full cycle (60°) per 1.2s so each rainbow loop travels
     continuously around the ring. full 360° rotation visually cycles
     the pattern 6 times — identical to itself, so no seam. */
  animation: fa-toggle-chase-sweep 1.2s linear infinite;
  pointer-events: none;
}
@keyframes fa-toggle-chase-sweep {
  to { --fa-chase-angle: 60deg; }
}

/* RIPPLE variant — two staggered concentric rings expanding outward from the
   launcher like a radar ping. drawn with ::before and ::after pseudo-elements
   sharing the same scale+fade animation, offset by half the duration so a new
   ring starts the moment the previous one is halfway out. baseline halo
   animation is disabled so the focus stays on the expanding rings. */
.fa-widget-toggle--tutorial-glow.fa-highlight-style-ripple {
  animation: none;
  overflow: visible;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-ripple::before,
.fa-widget-toggle--tutorial-glow.fa-highlight-style-ripple::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 3px solid rgba(72, 150, 220, 0.7);
  animation: fa-toggle-ripple 1.6s ease-out infinite;
  pointer-events: none;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-ripple::after {
  animation-delay: 0.8s;
}
@keyframes fa-toggle-ripple {
  0%   { transform: scale(1);   opacity: 0.75; border-width: 3px; }
  100% { transform: scale(1.9); opacity: 0;    border-width: 1px; }
}

/* MAGENTA variant — single-color glow that softly breathes in and out. no
   rainbow, no rotation: just a steady magenta presence with a gentle
   brightness rhythm. good for users who find the rainbow animations busy
   but still want a clear "look at me" cue. */
.fa-widget-toggle--tutorial-glow.fa-highlight-style-magenta {
  animation: fa-toggle-magenta-breathe 1.4s ease-in-out infinite;
}
@keyframes fa-toggle-magenta-breathe {
  0%, 100% {
    box-shadow:
      0 0 0 3px rgba(213, 0, 249, 0.40),
      0 0 16px 4px rgba(213, 0, 249, 0.35),
      0 4px 12px rgba(0, 92, 153, 0.28);
  }
  50% {
    box-shadow:
      0 0 0 6px rgba(255, 77, 255, 0.60),
      0 0 32px 10px rgba(213, 0, 249, 0.55),
      0 4px 12px rgba(0, 92, 153, 0.28);
  }
}

/* AURORA variant — slow drifting wash of soft greens, teals, and purples
   that undulate around the button like northern lights. two layers:
     ::before — conic-gradient of aurora colors, drifts clockwise at a
                leisurely 5s cycle so the motion reads as "atmospheric"
                rather than the chase's urgent sweep
     ::after  — same palette offset half-rotation so highlights never fully
                collapse to darkness on any side
   heavy blur melts the color bands into flowing washes; independent
   opacity breath on each layer adds subtle shimmer. */
/* AURORA — two counter-rotating wash layers with three brighter flare
   peaks embedded along the sweep. the two layers drift at different
   speeds (7s vs 5s), have asymmetric color stops, and breathe both
   opacity AND blur so the halo reads as organic sheets of light with
   occasional flares rather than an even mechanical rotation. */
@property --fa-aurora-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
@property --fa-aurora-angle-b {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-aurora {
  animation: none;
  overflow: visible;
}
.fa-widget-toggle--tutorial-glow.fa-highlight-style-aurora::before,
.fa-widget-toggle--tutorial-glow.fa-highlight-style-aurora::after {
  content: '';
  position: absolute;
  inset: -12px;
  border-radius: inherit;
  padding: 7px;
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
}
/* primary layer — slow drift, green flare at ~45°, magenta flare at ~215°.
   irregular angular spacing so the colors don't feel evenly distributed. */
.fa-widget-toggle--tutorial-glow.fa-highlight-style-aurora::before {
  background: conic-gradient(
    from var(--fa-aurora-angle),
    rgba(120, 255, 200, 0.40)    0deg,
    rgba(200, 255, 220, 0.95)   45deg,
    rgba(140, 230, 220, 0.55)   90deg,
    rgba(100, 220, 240, 0.45)  140deg,
    rgba(160, 180, 255, 0.55)  185deg,
    rgba(220, 180, 255, 0.90)  215deg,
    rgba(210, 140, 255, 0.45)  260deg,
    rgba(140, 220, 200, 0.55)  320deg,
    rgba(120, 255, 200, 0.40)  360deg
  );
  filter: blur(5px);
  animation:
    fa-toggle-aurora-drift 7s linear infinite,
    fa-toggle-aurora-flare 3.6s ease-in-out infinite alternate;
}
/* secondary layer — counter-rotates faster with its own violet flare at
   ~305° so the two layers' peaks cross rather than sync. */
.fa-widget-toggle--tutorial-glow.fa-highlight-style-aurora::after {
  background: conic-gradient(
    from var(--fa-aurora-angle-b),
    rgba(140, 220, 200, 0.30)    0deg,
    rgba(180, 240, 220, 0.50)   70deg,
    rgba(160, 200, 255, 0.40)  160deg,
    rgba(200, 160, 240, 0.50)  240deg,
    rgba(240, 200, 255, 0.95)  305deg,
    rgba(140, 220, 200, 0.30)  360deg
  );
  filter: blur(7px);
  animation:
    fa-toggle-aurora-drift-b 5s linear -2s infinite reverse,
    fa-toggle-aurora-flare 2.7s ease-in-out -1.3s infinite alternate-reverse;
}
@keyframes fa-toggle-aurora-drift {
  to { --fa-aurora-angle: 360deg; }
}
@keyframes fa-toggle-aurora-drift-b {
  to { --fa-aurora-angle-b: 360deg; }
}
/* breath + flare in one — opacity and blur ease together so bright peaks
   seem to swell and dissolve, giving the halo an organic, non-mechanical
   feel. */
@keyframes fa-toggle-aurora-flare {
  0%   { opacity: 0.40; filter: blur(8px); }
  40%  { opacity: 0.85; filter: blur(4px); }
  100% { opacity: 1.00; filter: blur(3px); }
}

.fa-widget-panel {
  position: fixed;
  z-index: 2147483001;
  border: 1px solid #d9dee5;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
}

.fa-widget-panel--mobile {
  right: 0;
  bottom: 0;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* shrinks when iOS keyboard opens */
  max-width: none;
  max-height: none;
  border-radius: 0;
  border: none;
  overflow: hidden;
}

.fa-widget-header {
  flex-shrink: 0;
  height: 52px;
  padding: 0 8px 0 16px;
  border-bottom: 1px solid #e3e6ea;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* transition the height-related props so the mobile collapse feels
     smooth when the user focuses the textarea (see --input-focused below). */
  transition: height 0.2s ease, padding 0.2s ease, border-bottom-color 0.2s ease;
}

/* MOBILE: when the user taps into the FA input, partially collapse the
   header so the iOS keyboard + message list + input row share the visible
   viewport cleanly, WITHOUT hiding the close button. 36px keeps the × comfortably
   tappable (Apple HIG minimum is 44, but this bar is only visible during active
   typing where the user's thumb is on-screen). the title fades out to remove
   visual noise; the divider disappears too so the bar reads as part of the
   content area rather than a separate chrome strip. desktop is unaffected. */
.fa-widget-panel--input-focused .fa-widget-header {
  height: 36px;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom-color: transparent;
  overflow: hidden;
}
.fa-widget-title {
  transition: opacity 0.15s ease;
}
.fa-widget-panel--input-focused .fa-widget-title {
  opacity: 0;
  pointer-events: none;
}

.fa-widget-header--draggable {
  cursor: grab;
  user-select: none;
}

.fa-widget-header--draggable:active {
  cursor: grabbing;
}

.fa-widget-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.fa-widget-btn-icon,
.fa-widget-close {
  color: #4c5a67;
  text-decoration: none;
  font-size: 18px;
  line-height: 1;
}

.fa-widget-btn-icon:hover,
.fa-widget-close:hover {
  color: #233746;
}

.fa-widget-title {
  color: #274a63;
  font-size: 15px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fa-widget-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* four-corner resize grips. all four share the same subtle diagonal-stripe
   marker — a faint `/` or `\` pointing outward from each corner so the
   affordance reads the same everywhere. top grips sit above the header
   (z-index: 1) so mousedown lands on them, not on the drag-bar. cursor
   is picked per-corner pair (nwse vs nesw). */
.fa-widget-resize-handle {
  position: absolute;
  width: 18px;
  height: 18px;
}

.fa-widget-resize-handle--nw,
.fa-widget-resize-handle--se {
  cursor: nwse-resize;
}
.fa-widget-resize-handle--ne,
.fa-widget-resize-handle--sw {
  cursor: nesw-resize;
}

.fa-widget-resize-handle--nw {
  top: 0;
  left: 0;
  border-radius: 14px 0 0 0;
  z-index: 1;
  background: linear-gradient(315deg, transparent 0 55%, rgba(0, 0, 0, 0.06) 55% 65%, transparent 65%);
}
.fa-widget-resize-handle--ne {
  top: 0;
  right: 0;
  border-radius: 0 14px 0 0;
  z-index: 1;
  background: linear-gradient(45deg, transparent 0 55%, rgba(0, 0, 0, 0.06) 55% 65%, transparent 65%);
}
.fa-widget-resize-handle--sw {
  bottom: 0;
  left: 0;
  border-radius: 0 0 0 14px;
  background: linear-gradient(225deg, transparent 0 55%, rgba(0, 0, 0, 0.06) 55% 65%, transparent 65%);
}
.fa-widget-resize-handle--se {
  bottom: 0;
  right: 0;
  border-radius: 0 0 14px 0;
  background: linear-gradient(135deg, transparent 0 55%, rgba(0, 0, 0, 0.06) 55% 65%, transparent 65%);
}

.fa-widget-resize-handle:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.fa-widget-expand-enter-active,
.fa-widget-expand-leave-active {
  transition: opacity 220ms ease, transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
  transform-origin: bottom right;
}

.fa-widget-expand-enter,
.fa-widget-expand-leave-to {
  opacity: 0;
  transform: translateY(18px) scale(0.96);
}

.fa-launcher-pulse-enter-active,
.fa-launcher-pulse-leave-active {
  transition: opacity 180ms ease, transform 200ms ease;
}

.fa-launcher-pulse-enter,
.fa-launcher-pulse-leave-to {
  opacity: 0;
  transform: scale(0.94);
}

@media (max-width: 767px) {
  .fa-widget-toggle {
    right: 12px;
    bottom: 12px;
    padding: 10px 14px;
    font-size: 14px;
  }

  /* keep the icon-only circle symmetrical on mobile — without this, the
     pill's 10px 14px padding leaks through and the circle becomes an oval. */
  .fa-widget-toggle--icon-only {
    padding: 0;
  }

  .fa-widget-expand-enter-active,
  .fa-widget-expand-leave-active {
    transform-origin: center bottom;
  }
}
</style>
